// Structured address check through /address_validation/validateAddress (the
// route the storefront checkout uses; the back office answers it when the SA
// provider is "deconetwork"). Used by the Company form for the ship-from and
// billing addresses (card #627).
//
// Jake's rule: the check WARNS and OFFERS a correction; it never blocks a
// save and never applies a correction by itself. So this never throws, and a
// failed or unavailable check resolves to {status: "unavailable"}.
//
//   verified     the answer matches what was typed (case, punctuation and a
//                ZIP+4 extension aside)
//   suggest      a different address came back: {suggestion, zipOrStateChanged}
//   unverified   the service ANSWERED that it cannot match the address
//   unavailable  outage, budget, error or anything unreadable
//
// Never logs address values.

const PATH = "/address_validation/validateAddress";

const text = (v) => String(v ?? "").trim();
const loose = (v) => text(v).toUpperCase().replace(/[^A-Z0-9]/g, "");
const zip5 = (v) => text(v).slice(0, 5);

export function hasCheckableAddress(addr) {
  return ["line1", "city", "state", "postal_code"].every((k) => text(addr?.[k]) !== "");
}

export async function checkAddress(http, addr) {
  const country = text(addr?.country) || "US";
  let data;
  try {
    const response = await http.post(PATH, {
      location: {
        street_and_number: text(addr.line1),
        suite_unit_etc: text(addr.line2),
        city: text(addr.city),
        region: text(addr.state),
        zip_code: text(addr.postal_code),
        country,
        stateAbbreviation: text(addr.state),
        countryAbbreviation: country,
      },
    });
    data = response && response.data;
  } catch (e) {
    return { status: "unavailable" };
  }
  if (!data || typeof data !== "object" || data.error) return { status: "unavailable" };
  if (data._unmatched === true) return { status: "unverified" };
  if (data._fallback === true || data._unparseable === true) return { status: "unavailable" };

  const suggestion = {
    line1: text(data.street_and_number),
    line2: text(data.suite_unit_etc),
    city: text(data.city),
    state: text(data.region),
    postal_code: text(data.zip_code),
  };
  if (!suggestion.line1 || !suggestion.city || !suggestion.state || !suggestion.postal_code) {
    return { status: "unavailable" };
  }
  const same =
    loose(suggestion.line1) === loose(addr.line1) &&
    loose(suggestion.line2) === loose(addr.line2) &&
    loose(suggestion.city) === loose(addr.city) &&
    loose(suggestion.state) === loose(addr.state) &&
    zip5(suggestion.postal_code) === zip5(addr.postal_code);
  if (same) return { status: "verified" };
  return {
    status: "suggest",
    suggestion,
    zipOrStateChanged: data.zip_or_state_changed === true,
  };
}
